import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import scoreAPI from '../services/scoreApi';
import { publicAssetUrl } from '../utils/publicAssetUrl';

const Wrap = styled.div`
  margin-top: ${(p) => (p.$contactFocus ? '0.35rem' : '0.5rem')};
  margin-bottom: ${(p) => (p.$contactFocus ? '0.55rem' : '0')};
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
`;

const Logo = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid #e2e8f0;
  flex-shrink: 0;
`;

const LogoPh = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e2e8f0;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  flex-shrink: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
`;

const Sub = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 4px;
`;

const Body = styled.div`
  padding: ${(p) => (p.$contactFocus ? '12px 16px 14px' : '14px 16px 16px')};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 14px;
`;

const StatCell = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  text-align: center;
`;

const StatVal = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: #043663;
`;

const StatLbl = styled.div`
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #64748b;
  margin-top: 2px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
`;

const PlayerCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
  display: flex;
  gap: 12px;
`;

const PAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const PAvatarPh = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #475569;
  flex-shrink: 0;
`;

const PMeta = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
`;

const PName = styled.div`
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
`;

const DL = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
  font-size: 0.75rem;
`;

const DT = styled.dt`
  color: #94a3b8;
  margin: 0;
  font-weight: 600;
`;

const DD = styled.dd`
  margin: 0;
  color: #1e293b;
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.78rem;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
`;

const Muted = styled.p`
  margin: 0;
  color: #94a3b8;
  font-size: 0.85rem;
`;

function fmt(v) {
  if (v == null || v === '') return '—';
  return String(v);
}

const ContactLink = styled.a`
  color: #043663;
  text-decoration: none;
  word-break: break-all;
  &:hover {
    text-decoration: underline;
  }
`;

const CaptainBadge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  background: rgba(4, 54, 99, 0.1);
  color: #043663;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  vertical-align: middle;
`;

/** Inline opponent team sheet (no modal chrome). */
export default function OpponentTeamSheetPanel({ matchId, teamId, active = true, contactFocus = false }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!matchId || !teamId) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await scoreAPI.getOpponentTeamSheet(matchId, teamId);
      if (res?.success && res.data) setData(res.data);
      else setErr(res?.error || 'load failed');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'load failed');
    } finally {
      setLoading(false);
    }
  }, [matchId, teamId]);

  useEffect(() => {
    if (active && matchId && teamId) load();
  }, [active, matchId, teamId, load]);

  const langLabel = (code) => {
    if (!code) return '—';
    try {
      const dn = new Intl.DisplayNames([i18n.language || 'en'], { type: 'language' });
      return dn.of(String(code).replace('_', '-').slice(0, 5)) || code;
    } catch {
      return code;
    }
  };

  const team = data?.team;
  const logoSrc = team?.logoUrl ? publicAssetUrl(team.logoUrl) : '';
  const st = data?.standing;

  return (
    <Wrap $contactFocus={contactFocus}>
      {!contactFocus ? (
        <Head>
          {logoSrc ? <Logo src={logoSrc} alt="" /> : <LogoPh>{(team?.name || '?').trim().slice(0, 1).toUpperCase()}</LogoPh>}
          <div>
            <Title>{team?.name || t('teamDashboardPage.opponentSheetFallbackTeam')}</Title>
            {team?.skillLevel ? <Sub>{t('teamDashboardPage.opponentSheetTeamLevel', { level: team.skillLevel })}</Sub> : null}
          </div>
        </Head>
      ) : null}
      <Body $contactFocus={contactFocus}>
        {loading ? <Muted>{t('teamDashboardPage.opponentSheetLoading')}</Muted> : null}
        {err && !loading ? <Muted style={{ color: '#b91c1c' }}>{err}</Muted> : null}

        {!loading && !err && Array.isArray(data?.players) && data.players.length > 0 ? (
          <>
            <SectionTitle>
              {contactFocus
                ? t('teamDashboardPage.opponentSheetContactPlayers')
                : t('teamDashboardPage.opponentSheetPlayers')}
            </SectionTitle>
            {data.players.map((p) => {
              const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || '—';
              const pic =
                p.profile_picture_url != null && String(p.profile_picture_url).trim()
                  ? publicAssetUrl(p.profile_picture_url)
                  : '';
              const initials = name
                .split(/\s+/)
                .map((x) => x[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
              const email = p.email && String(p.email).trim();
              const phone = p.phone_number && String(p.phone_number).trim();
              return (
                <PlayerCard key={p.member_id || name}>
                  {pic ? <PAvatar src={pic} alt="" /> : <PAvatarPh>{initials || '?'}</PAvatarPh>}
                  <PMeta>
                    <PName>
                      {name}
                      {p.position === 'captain' ? (
                        <CaptainBadge>{t('teamDashboardPage.opponentSheetCaptain')}</CaptainBadge>
                      ) : null}
                    </PName>
                    <DL>
                      <DT>{t('teamDashboardPage.opponentSheetEmail')}</DT>
                      <DD>
                        {email ? <ContactLink href={`mailto:${email}`}>{email}</ContactLink> : '—'}
                      </DD>
                      <DT>{t('teamDashboardPage.opponentSheetPhone')}</DT>
                      <DD>
                        {phone ? <ContactLink href={`tel:${phone}`}>{phone}</ContactLink> : '—'}
                      </DD>
                      {!contactFocus ? (
                        <>
                          <DT>{t('teamDashboardPage.opponentSheetLanguage')}</DT>
                          <DD>{langLabel(p.ui_locale)}</DD>
                          <DT>{t('teamDashboardPage.opponentSheetCanton')}</DT>
                          <DD>{fmt(p.home_canton)}</DD>
                          <DT>{t('teamDashboardPage.opponentSheetHomeClub')}</DT>
                          <DD>{fmt(p.home_club)}</DD>
                          <DT>{t('teamDashboardPage.opponentSheetLevel')}</DT>
                          <DD>{fmt(p.skill_level)}</DD>
                          <DT>{t('teamDashboardPage.opponentSheetHand')}</DT>
                          <DD>{fmt(p.dominant_hand)}</DD>
                          <DT>{t('teamDashboardPage.opponentSheetSide')}</DT>
                          <DD>{fmt(p.preferred_court_side)}</DD>
                        </>
                      ) : null}
                    </DL>
                  </PMeta>
                </PlayerCard>
              );
            })}
          </>
        ) : null}

        {!contactFocus && !loading && !err && st ? (
          <>
            <SectionTitle>{t('teamDashboardPage.opponentSheetStats')}</SectionTitle>
            <StatGrid>
              <StatCell>
                <StatVal>{fmt(st.wins)}</StatVal>
                <StatLbl>{t('teamDashboardPage.opponentSheetStatWins')}</StatLbl>
              </StatCell>
              <StatCell>
                <StatVal>{fmt(st.losses)}</StatVal>
                <StatLbl>{t('teamDashboardPage.opponentSheetStatLosses')}</StatLbl>
              </StatCell>
              <StatCell>
                <StatVal>{fmt(st.points)}</StatVal>
                <StatLbl>{t('teamDashboardPage.opponentSheetStatPoints')}</StatLbl>
              </StatCell>
            </StatGrid>
          </>
        ) : null}

        {!contactFocus && !loading && !err && Array.isArray(data?.recentResults) && data.recentResults.length > 0 ? (
          <>
            <SectionTitle style={{ marginTop: 8 }}>{t('teamDashboardPage.opponentSheetRecent')}</SectionTitle>
            {data.recentResults.map((r) => (
              <ResultRow key={r.matchId}>
                <span>
                  {t('teamDashboardPage.vsOpponent', { name: r.opponentName })}
                  {r.matchDate ? <span style={{ color: '#94a3b8' }}> · {r.matchDate}</span> : null}
                </span>
                <strong style={{ color: '#0f172a' }}>{r.scoreLine}</strong>
              </ResultRow>
            ))}
          </>
        ) : null}
      </Body>
    </Wrap>
  );
}
